import * as React from 'react';
import { FiCheck, FiChevronDown } from 'react-icons/fi';

import { cn } from '../../lib/utils';
import { Button } from './button-simple';
import { Input } from './input-simple';

export interface ComboboxOption {
  value: string;
  label: string;
}

export interface ComboboxProps {
  id: string;
  value: string;
  options: ComboboxOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
}

export function Combobox({
  id,
  value,
  options,
  onChange,
  placeholder = 'Select option',
  searchPlaceholder = 'Search...',
  emptyText = 'No option found.',
  disabled = false,
  required = false,
  className,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const rootRef = React.useRef<HTMLDivElement | null>(null);

  const selectedOption = React.useMemo(() => options.find((option) => option.value === value), [options, value]);

  const filteredOptions = React.useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) return options;

    return options.filter((option) => option.label.toLowerCase().includes(normalizedSearch));
  }, [options, search]);

  React.useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      if (!rootRef.current) return;
      if (rootRef.current.contains(event.target as Node)) return;
      setOpen(false);
      setSearch('');
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <input id={id} value={value} onChange={() => undefined} required={required} className="sr-only" tabIndex={-1} aria-hidden="true" />
      <Button
        type="button"
        variant="outline"
        role="combobox"
        aria-expanded={open}
        aria-controls={`${id}-listbox`}
        onClick={() => {
          if (disabled) return;
          setOpen((current) => !current);
        }}
        disabled={disabled}
        className="w-full justify-between border-input bg-background text-left font-normal"
      >
        <span className="truncate">{selectedOption?.label ?? placeholder}</span>
        <FiChevronDown className="h-4 w-4 shrink-0 opacity-60" />
      </Button>

      {open && (
        <div className="absolute z-50 mt-2 w-full rounded-md border border-border bg-popover p-2 shadow-md">
          <Input
            id={`${id}-search`}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={searchPlaceholder}
            className="mb-2"
            autoFocus
          />
          <div id={`${id}-listbox`} role="listbox" className="max-h-56 overflow-auto">
            {filteredOptions.length === 0 && <div className="px-2 py-2 text-sm text-muted-foreground">{emptyText}</div>}
            {filteredOptions.map((option) => {
              const isSelected = option.value === value;

              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => {
                    onChange(option.value);
                    setOpen(false);
                    setSearch('');
                  }}
                  className={cn(
                    'flex w-full items-center justify-between rounded-sm px-2 py-2 text-left text-sm transition-colors hover:bg-accent hover:text-accent-foreground',
                    isSelected && 'bg-accent text-accent-foreground'
                  )}
                >
                  <span>{option.label}</span>
                  {isSelected && <FiCheck className="h-4 w-4" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
